import { featuresManager } from "../../core/service/services";
import { useObservable } from "../../utils/observable";
import { getTotalOfDetailedAccounting } from "../../utils/total-amount";
import type { AuthorizationHold } from "../authorization-hold/authorization-hold";
import type { Transaction } from "./transaction/transaction";
import { TransactionLinks } from "./transaction/transaction-links";

type TransactionOrAuthorizationHold = Transaction | AuthorizationHold;

export const useTransactionLinks = () => {
  const features = useObservable(featuresManager.features);

  const getLinks = (transaction?: TransactionOrAuthorizationHold) =>
    // Filter to keep only expenses
    // if (transaction && transaction.amount && transaction.amount.value > 0) {
    // 	return [];
    // }

    transaction?.links ?? [];
  const isOutgoing = (transaction: TransactionOrAuthorizationHold) => {
    if (transaction.detailedAccounting) {
      return getTotalOfDetailedAccounting(transaction.detailedAccounting) < 0;
    }
    return Number(transaction.amount?.value) < 0;
  };

  const canViewJustification = (transaction: TransactionOrAuthorizationHold) =>
    (canGetSpendings(transaction) || canGetAttachments(transaction)) && isOutgoing(transaction);

  const canJustify = (transaction: TransactionOrAuthorizationHold) => {
    if (!features.manageCategorizationSpendings) {
      return false;
    }
    return (!transaction.justified || !transaction.qualified) && isOutgoing(transaction);
  };

  const canShowJustifyLabel = (transaction: TransactionOrAuthorizationHold) => {
    if (!features.manageCategorizationSpendings) {
      return false;
    }
    if (transaction.locked) {
      return false;
    }
    return (!transaction.justified || !transaction.qualified) && isOutgoing(transaction);
  };

  const canEditJustification = (transaction: TransactionOrAuthorizationHold) => {
    if (!features.manageCategorizationSpendings) {
      return false;
    }

    const links = getLinks(transaction);
    return (
      links.findIndex(
        (link) => link.rel === TransactionLinks.GetSpendings || link.rel === TransactionLinks.GetAttachments,
      ) > -1
    );
  };

  const canShowAttachments = (transaction: TransactionOrAuthorizationHold) => {
    const links = getLinks(transaction);
    return (
      links.findIndex(
        (link) => link.rel === TransactionLinks.AddAttachment || link.rel === TransactionLinks.GetAttachments,
      ) > -1
    );
  };

  const canShowSpendings = (transaction: TransactionOrAuthorizationHold) => {
    const links = getLinks(transaction);
    return (
      links.findIndex(
        (link) => link.rel === TransactionLinks.AddSpending || link.rel === TransactionLinks.GetSpendings,
      ) > -1
    );
  };

  const canGetSpendings = (transaction: TransactionOrAuthorizationHold) => {
    const links = getLinks(transaction);
    return links.findIndex((link) => link.rel === TransactionLinks.GetSpendings) > -1;
  };

  const canGetAttachments = (transaction: TransactionOrAuthorizationHold) => {
    const links = getLinks(transaction);
    return links.findIndex((link) => link.rel === TransactionLinks.GetAttachments) > -1;
  };

  const canAddSpending = (transaction: TransactionOrAuthorizationHold) => {
    const links = getLinks(transaction);
    return links.findIndex((link) => link.rel === TransactionLinks.AddSpending) > -1;
  };

  const canAddAttachment = (transaction: TransactionOrAuthorizationHold) => {
    const links = getLinks(transaction);
    return links.findIndex((link) => link.rel === TransactionLinks.AddAttachment) > -1;
  };

  const canGetBothAttachmentsAndSpendings = (transaction: TransactionOrAuthorizationHold) => {
    if (!features.manageCategorizationSpendings) {
      return false;
    }

    return canGetAttachments(transaction) && canGetSpendings(transaction);
  };

  return {
    isOutgoing,
    canJustify,
    canShowJustifyLabel,
    canEditJustification,
    canShowAttachments,
    canShowSpendings,
    canGetSpendings,
    canGetAttachments,
    canAddSpending,
    canAddAttachment,
    canViewJustification,
    canGetBothAttachmentsAndSpendings,
  };
};
